"use client";

import Cookie from "js-cookie";
import { motion } from "framer-motion";
import { usePathname, useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { Product as ProductType } from "@/@types";
import { CategoryImage, EMPTY_FACETS, Facets } from "@/@types/filters";
import Button from "@/components/Button";
import Filters from "@/components/Filters";
import Product from "@/components/Product";
import useProductFilters from "@/hooks/useProductFilters";
import {
  PAGE_SIZE,
  ProductsQuery,
  appendUnique,
  fetchProducts,
  parseFiltersFromParams,
  queryKey,
  replacePageInUrl,
} from "@/lib/products";
import { defineImageUrl } from "@/utils";

import styles from "./ProductList.module.scss";

/** O que a página lista (categoria, novidades...). Filtros e janela vêm da URL. */
export type ListingQuery = Omit<ProductsQuery, "filters" | "page" | "offset" | "limit">;

interface LoadedList {
  /** Identidade da lista (caminho + filtros, sem a página). */
  key: string;
  items: ProductType[];
  total: number;
}

// Listas estendidas pelo "Ver mais", indexadas pela URL com a página. Ficam em
// memória enquanto o app está aberto: ao voltar de um produto, a lista volta
// inteira na hora, sem refazer pedidos. Um reload zera o mapa, e aí o servidor
// renderiza a lista acumulada da URL.
const loadedLists = new Map<string, LoadedList>();
const MAX_REMEMBERED_LISTS = 20;

function pageKey(pathname: string, params: URLSearchParams) {
  const sorted = new URLSearchParams(params);
  sorted.sort();
  return `${pathname}?${sorted.toString()}`;
}

function rememberList(list: LoadedList) {
  const key = pageKey(window.location.pathname, new URLSearchParams(window.location.search));

  loadedLists.delete(key);
  loadedLists.set(key, list);
  if (loadedLists.size > MAX_REMEMBERED_LISTS) {
    loadedLists.delete(loadedLists.keys().next().value as string);
  }
}

interface Props {
  products: ProductType[];
  total: number;
  label?: string;
  showFilter?: boolean;
  facets?: Facets;
  images?: CategoryImage[];
  /** Semelhantes da busca semântica, exibidos depois dos resultados diretos. */
  semanticProducts?: ProductType[];
  /** `total` é um piso (teto de candidatos da busca): exibe "250+". */
  totalCapped?: boolean;
  /** Resultados de uma consulta anterior enquanto a nova carrega: esmaece a grade. */
  stale?: boolean;
  /** Listagem do servidor: com ela, o "Ver mais" busca só a próxima página. */
  query?: ListingQuery;
  /** Quem é dono dos produtos (a busca) carrega a próxima página por conta própria. */
  onLoadMore?: () => Promise<void>;
}

export default function ProductList(props: Props) {
  const {
    products,
    total = 0,
    label = "Esta lista está vazia",
    showFilter = false,
    facets = EMPTY_FACETS,
    images = [],
    semanticProducts = [],
    totalCapped = false,
    stale = false,
    query,
    onLoadMore,
  } = props;

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { activeCount, clear } = useProductFilters();

  const listKey = `${pathname}?${queryKey(new URLSearchParams(searchParams.toString()))}`;
  const listKeyRef = useRef(listKey);
  listKeyRef.current = listKey;

  // Em memória só existe depois de uma navegação no cliente (o mapa nasce vazio
  // num carregamento completo), então restaurar aqui não quebra a hidratação.
  const [loaded, setLoaded] = useState<LoadedList>(() => {
    const remembered =
      typeof window !== "undefined" && query
        ? loadedLists.get(pageKey(pathname, new URLSearchParams(searchParams.toString())))
        : undefined;

    return remembered?.key === listKey ? remembered : { key: listKey, items: products, total };
  });
  const [loadingMore, setLoadingMore] = useState(false);

  // A busca controla a própria lista; nas listagens, outra lista (categoria,
  // filtro) recomeça do que o servidor mandou.
  const current: LoadedList =
    !onLoadMore && loaded.key === listKey ? loaded : { key: listKey, items: products, total };

  useEffect(() => {
    if (!onLoadMore && loaded.key !== listKey) setLoaded({ key: listKey, items: products, total });
  }, [onLoadMore, loaded.key, listKey, products, total]);

  async function loadNextPage() {
    if (onLoadMore) return onLoadMore();
    if (!query) return;

    const startedAt = current;
    const response = await fetchProducts(
      {
        ...query,
        filters: parseFiltersFromParams(new URLSearchParams(searchParams.toString())),
        offset: startedAt.items.length,
        limit: PAGE_SIZE,
      },
      { secret: Cookie.get("secret"), auth: Cookie.get("auth") }
    );

    // Filtro ou categoria trocou enquanto o pedido voltava: descarta.
    if (listKeyRef.current !== startedAt.key) return;

    const items = appendUnique(startedAt.items, response.products ?? []);
    const next: LoadedList = {
      key: startedAt.key,
      items,
      // Sem nada novo, a lista acabou (o total pode ter mudado na API): para
      // aqui em vez de deixar um "Ver mais" que nunca termina.
      total: items.length > startedAt.items.length ? response.total ?? startedAt.total : items.length,
    };

    setLoaded(next);
    replacePageInUrl(Math.ceil(items.length / PAGE_SIZE));
    rememberList(next);
  }

  async function handleLoadMore() {
    setLoadingMore(true);

    try {
      await loadNextPage();
    } catch {
      toast.error("Não foi possível carregar mais produtos. Tente de novo.");
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if ((window as any).gtag) (window as any).gtag("event", "view_item_list");
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const shown = current.items;
  const hasMore = Boolean(onLoadMore || query) && current.total > shown.length;
  // A API pode devolver só semelhantes quando a busca direta não acha nada.
  const listed = [...shown, ...semanticProducts];

  return (
    <>
      {showFilter && (
        <Filters
          facets={facets}
          total={current.total}
          totalCapped={totalCapped}
        />
      )}

      {listed.length > 0 ? (
        <>
          <motion.div
            layoutScroll
            className={styles.ProductList}
            data-stale={stale || undefined}
          >
            {listed.map((product, index) => {
              const imageIndex = Math.floor(index / 12);

              return (
                // Os semelhantes vêm de chunks; com vários por produto, o id pode repetir.
                <React.Fragment key={`${product.id}-${index}`}>
                  {index === shown.length && (
                    <h2 className={styles.ProductList__sectionTitle}>Produtos semelhantes</h2>
                  )}

                  <Product product={product} />

                  {(index + 1) % 12 === 0 && images[imageIndex] && (
                    <div className={styles.ProductList__banner}>
                      <img
                        src={defineImageUrl(images[imageIndex].image.url)}
                        alt=""
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </motion.div>

          {hasMore && (
            <div className={styles.ProductList__loader}>
              <Button title="Ver mais" onClick={handleLoadMore} loading={loadingMore} />

              <div className={styles.ProductList__counter}>
                Mostrando <strong>{shown.length}</strong> de{" "}
                <strong>
                  {current.total}
                  {totalCapped && "+"}
                </strong>{" "}
                produtos
              </div>

              <div className={styles.ProductList__progress}>
                <div
                  className={styles.ProductList__progressBar}
                  style={{
                    width: current.total > 0 ? `${(shown.length / current.total) * 100}%` : "0%",
                  }}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.ProductList__empty}>
          {activeCount > 0 ? (
            <>
              <p>Nenhum produto encontrado com esses filtros.</p>
              <Button title="Limpar filtros" onClick={clear} />
            </>
          ) : (
            <p>{label}</p>
          )}
        </div>
      )}
    </>
  );
}

export { PAGE_SIZE };
